import { EM_DASH } from '../lib/typography';
import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ACCENT_GOLD, TEXT_PRIMARY,
  BG_ATMOSPHERE, VIGNETTE, CONTENT_BG_DEFAULT, OrnamentalDivider,
} from '../lib/medievalTheme';
import { CloseButton } from './ui/navButtons';
import { UI, playUI } from '../utils/arena/uiSounds';
import LevelRow from './progression/LevelRow';
import ClaimPulse from './progression/ClaimPulse';
import {
  getProgression, claimProgressionGold, claimProgressionSkill,
} from '../utils/arena/profileApi';
import {
  GOLD_GRANT_LEVELS, SKILL_POINT_GRANT_LEVELS, DECK_GRANT_LEVELS,
  GOLD_PER_LEVEL, SKILL_POINT_PER_GRANT, MAX_LEVEL,
} from '../utils/arena/profileDefaults';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { t } from '../localization/t';

// Vertical scrollable Progression screen. Every level (1..MAX_LEVEL)
// renders as a row inline. No click-to-focus model. Each row shows
// the level coin on the left and any reward cells for that level
// inline to the right. The entire screen body scrolls so a player
// can walk their entire mastery path top to bottom in one motion.
//
// Replaces the LevelUpOverlay flow entirely (post-match level-up,
// login replay, hub access, L1 first-run). Deck cells route to the
// existing ArenaStarterPicker via the parent's onClaimDeck callback.

const KEYFRAMES = `
@keyframes progSpin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

export default class ProgressionScreen extends Component {
  state = {
    snapshot: null,
    claimingCell: null,    // `${level}-${type}` to suppress double-clicks
    error: null,
    loading: true,
    // Active claim-pulse overlay anchored to the cell the player just
    // tapped. Cleared via setTimeout after the wave animation runs;
    // the `id` lets us swap mid-flight if two claims land back-to-back.
    pulse: null,
  };
  _pulseTimer = null;

  scrollContainerRef = null;
  _scrolledToCurrent = false;

  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.props.onClose?.());
    this.hydrate();
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
    if (this._pulseTimer) clearTimeout(this._pulseTimer);
  }

  triggerClaimPulse = (origin) => {
    if (!origin) return;
    if (this._pulseTimer) clearTimeout(this._pulseTimer);
    // The id changes per pulse so React tears down the previous
    // ClaimPulse component (and its motion timers) when a fast
    // second claim lands. Otherwise framer-motion would try to
    // diff the same instance and the wave would re-use mid-flight
    // tweens, looking visually broken.
    this.setState({ pulse: { ...origin, id: Date.now() + Math.random() } });
    // Slowest aura finishes at delay 0.32s + 1.25s = 1.57s; give it
    // 100ms breathing room so nothing visually clips on unmount.
    this._pulseTimer = setTimeout(() => this.setState({ pulse: null }), 1700);
  };

  componentDidUpdate(prevProps, prevState) {
    // Sync from parent snapshot pushes (e.g. after a deck claim
    // round-trips through the picker). The parent updates
    // arenaProfile.progression which we receive here.
    if (this.props.progression && this.props.progression !== prevProps.progression) {
      this.setState({ snapshot: this.props.progression });
    }
    if (this.props.progressionRefreshTag !== prevProps.progressionRefreshTag) {
      this.hydrate();
    }
    // After the first hydrate, auto-scroll to the player's current
    // level so they land mid-journey instead of staring at L1 every
    // time. Only runs once per mount.
    if (!this._scrolledToCurrent && this.state.snapshot && this.scrollContainerRef) {
      this._scrolledToCurrent = true;
      const node = this.scrollContainerRef.querySelector(`[data-level="${this.state.snapshot.level}"]`);
      if (node) {
        // Defer a frame so layout has settled.
        requestAnimationFrame(() => {
          node.scrollIntoView({ behavior: 'auto', block: 'center' });
        });
      }
    }
  }

  async hydrate() {
    try {
      const snapshot = await getProgression();
      this.setState({ snapshot, loading: false });
    } catch (err) {
      console.error('[ProgressionScreen] hydrate failed:', err);
      this.setState({ error: t('Could not load progression'), loading: false });
    }
  }

  applySnapshot(snapshot) {
    this.setState({ snapshot, claimingCell: null });
  }

  handleClose = () => {
    playUI(UI.CLICK);
    this.props.onClose?.();
  };

  handleCellClaim = async (level, type, origin) => {
    if (this.state.claimingCell) return;
    const cellKey = `${level}-${type}`;
    this.setState({ claimingCell: cellKey });

    if (type === 'deck') {
      // Deck claims open the picker via the parent. The picker's
      // confirm path round-trips through handleProgressionDeckChosen
      // and pushes a refreshed snapshot back via the `progression`
      // prop, which componentDidUpdate applies. Clear claimingCell
      // after a short window in case the picker is dismissed.
      playUI(UI.CONFIRM);
      this.props.onClaimDeck?.(level);
      setTimeout(() => {
        if (this.state.claimingCell === cellKey) this.setState({ claimingCell: null });
      }, 1800);
      return;
    }

    // Triumphant feedback. The gold-wave pulse fires immediately on
    // click. Server confirmation comes back below, but the player
    // shouldn't have to wait for the network round-trip to feel the
    // reward landing. If the claim ends up failing, the wave already
    // played; the error toast surfaces the rollback cleanly.
    this.triggerClaimPulse(origin);
    playUI(UI.PURCHASE, { volume: 0.95 });
    try {
      const res = type === 'gold'
        ? await claimProgressionGold(level)
        : await claimProgressionSkill(level);
      if (res?.progression) {
        this.applySnapshot(res.progression);
      } else {
        await this.hydrate();
      }
      if (type === 'gold' && typeof res?.coins === 'number') {
        this.props.onRewardClaimed?.({ type: 'gold', coins: res.coins });
      } else if (type === 'skill' && typeof res?.grantedSkillPoints === 'number') {
        this.props.onRewardClaimed?.({ type: 'skill', grantedSkillPoints: res.grantedSkillPoints });
      }
    } catch (err) {
      console.error('[ProgressionScreen] claim failed:', err);
      this.setState({ claimingCell: null, error: err.message || t('Claim failed') });
      setTimeout(() => this.setState({ error: null }), 2400);
    }
  };

  render() {
    const { snapshot, claimingCell, error, loading } = this.state;
    const max = snapshot?.maxLevel ?? MAX_LEVEL;

    return (
      <motion.div
        className="fixed inset-0 z-[300] flex flex-col"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.22 }}
        style={{
          background: BG_ATMOSPHERE,
          // When the deck picker is open on top of this screen, hide
          // the screen so the picker (which lives at z-[55], the
          // canonical modal layer) isn't covered by our z-[300]
          // wrapper. visibility:hidden preserves scroll state so the
          // player returns to the same row after picking.
          visibility: this.props.hiddenForPicker ? 'hidden' : 'visible',
        }}
      >
        <style>{KEYFRAMES}</style>
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background: `${CONTENT_BG_DEFAULT}, #0c0a08`,
 backgroundBlendMode: 'overlay, normal',
 }}
 />
 <div className="fixed inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

 {/* Canonical CloseButton from ui/navButtons. Same X every
 modal in the project uses (settings, admin, auction). The
 absolute wrapper pins it to the top-right corner. The
 z-index must be ABOVE the scroll surface (z-10) below or
 the scroll div eats the click since they live at the same
 stacking level and the scroll comes later in DOM order. */}
 <div
 className="absolute"
 style={{ top: '1.2cqw', right: '1.2cqw', zIndex: 50 }}
 >
 <CloseButton onClick={this.handleClose} title={t('Close')} />
 </div>

 {/* Scroll surface. The entire content (header included)
 scrolls together so the player sees the header again
 when they scroll back to the top. The fixed close button
 stays accessible at all times. */}
 <div
 ref={(el) => { this.scrollContainerRef = el; }}
 className="flex-1 relative z-10 overflow-y-auto overflow-x-hidden"
 >
 <div
 className="mx-auto"
 style={{
 maxWidth: '54cqw',
 padding: '2cqw 2cqw 4cqw',
 display: 'flex',
 flexDirection: 'column',
 gap: '0.6cqw',
 position: 'relative',
 }}
 >
 {/* Header. "Level X" + a short invitational subline.
 No XP-to-next caption: the player already knows that
 from the hub UI, and the screen's job here is to
 celebrate the level + invite the claim, not restate
 progress math. */}
 <motion.div
 className="text-center"
 initial={{ opacity: 0, y: -12 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.5 }}
 style={{ marginBottom: '2cqw' }}
 >
 <p
 style={{
 color: '#c6b486',
 fontSize: '0.7cqw',
 fontWeight: 700,
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 marginBottom: '0.4cqw',
 }}
 >
 {t('You have reached')}
 </p>
 <h1
 className="arena-heading"
 style={{
 fontSize: '3.4cqw',
 fontWeight: 700,
 letterSpacing: '0.04em',
 lineHeight: 1,
 background: 'linear-gradient(180deg, #fbe9b8 0%, #e8c466 55%, #b9893a 100%)',
 WebkitBackgroundClip: 'text',
 WebkitTextFillColor: 'transparent',
 backgroundClip: 'text',
 filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.85)) drop-shadow(0 0 24px rgba(212, 168, 67, 0.5))',
 marginBottom: '0.45cqw',
 }}
 >
          {t('Level {n}', { n: snapshot?.level ?? EM_DASH })}
 </h1>
 <OrnamentalDivider className="w-72 mx-auto" />
 <p
 style={{
 color: '#c6b486',
 letterSpacing: '0.06em',
 fontSize: '0.8cqw',
 fontStyle: 'italic',
 fontWeight: 500,
 marginTop: '0.6cqw',
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 }}
 >
 {t('Collect your rewards and strengthen your spells.')}
 </p>
 </motion.div>

 {/* Rows + continuous rail. The rail is one absolute-
 positioned element drawn behind every row's coin
 column, running from the first coin to the last so
 there are no segmentation gaps at row boundaries. */}
 {snapshot ? (
 <div style={{ position: 'relative' }}>
 <ContinuousRail
 max={max}
 playerLevel={snapshot.level}
 />
 {Array.from({ length: max }, (_, i) => {
 const level = i + 1;
 const cells = cellsForLevel(snapshot, level, claimingCell);
 return (
 <LevelRow
 key={level}
 level={level}
 isCurrent={level === snapshot.level}
 isLocked={level > snapshot.level}
 cells={cells}
 sorceryCards={this.props.sorceryCards}
 ownedDeckIds={this.props.ownedDeckIds || []}
 onClaim={this.handleCellClaim}
 />
 );
 })}
 </div>
 ) : loading ? (
 <div style={{ color: '#7d6e4c', fontSize: '0.85cqw', textAlign: 'center', padding: '4cqw' }}>
 {t('Loading…')}
 </div>
 ) : null}
 </div>
 </div>

 {/* Golden wave pulse. Emanates from the cell the player just
 claimed, with a brief screen-wide gold tint underneath.
 Keyed on pulse.id so a rapid second claim tears down the
 previous animation cleanly and a fresh one begins. */}
 {this.state.pulse ? (
 <ClaimPulse key={this.state.pulse.id} x={this.state.pulse.x} y={this.state.pulse.y} />
 ) : null}

 <AnimatePresence>
 {error ? (
 <motion.div
 key="err"
 className="absolute left-1/2"
 initial={{ y: -20, opacity: 0, x: '-50%' }}
 animate={{ y: 0, opacity: 1, x: '-50%' }}
 exit={{ y: -20, opacity: 0, x: '-50%' }}
 style={{
 top: '1.5cqw',
 padding: '0.5cqw 1.2cqw',
 background: 'linear-gradient(180deg, #3c160e 0%, #280c06 100%)',
 border: '1px solid #8a6322',
 borderRadius: 'var(--radius-sm)',
 color: '#e8d5a0',
 fontSize: '0.8cqw',
 zIndex: 15,
 }}
 >
 {error}
 </motion.div>
 ) : null}
 </AnimatePresence>
 </motion.div>
 );
 }
}

// ── Helpers ─────────────────────────────────────────────────────

function cellsForLevel(snapshot, level, claimingCell) {
 const cells = [];
 if (!snapshot) return cells;
 const goldGrants = snapshot.grantLevels?.gold ?? GOLD_GRANT_LEVELS;
 const skillGrants = snapshot.grantLevels?.skill ?? SKILL_POINT_GRANT_LEVELS;
 const deckGrants = snapshot.grantLevels?.deck ?? DECK_GRANT_LEVELS;
 const goldAmount = snapshot.perLevel?.goldAmount ?? GOLD_PER_LEVEL;
 const skillAmount = snapshot.perLevel?.skillAmount ?? SKILL_POINT_PER_GRANT;
 const playerLevel = snapshot.level;

 if (goldGrants.includes(level)) {
 cells.push({
 type: 'gold',
 amount: goldAmount,
 state: cellStateFor(level, playerLevel, snapshot.nextClaimable?.gold, claimingCell === `${level}-gold`),
      lockedLevel: level,
    });
  }
  if (skillGrants.includes(level)) {
    cells.push({
      type: 'skill',
      amount: skillAmount,
      state: cellStateFor(level, playerLevel, snapshot.nextClaimable?.skill, claimingCell === `${level}-skill`),
      lockedLevel: level,
    });
  }
  if (deckGrants.includes(level)) {
    cells.push({
      type: 'deck',
      amount: 1,
      state: cellStateFor(level, playerLevel, snapshot.nextClaimable?.deck, claimingCell === `${level}-deck`),
      lockedLevel: level,
    });
  }
  return cells;
}

function cellStateFor(level, playerLevel, nextClaimableLevel, isClaimingThisCell) {
  if (level > playerLevel) return 'locked';
  if (nextClaimableLevel === null || nextClaimableLevel === undefined) return 'received';
  if (level < nextClaimableLevel) return 'received';
  if (level === nextClaimableLevel) return isClaimingThisCell ? 'claiming' : 'claimable';
  // level > nextClaimableLevel but <= playerLevel. Must clear the
  // prior cell first under strict-in-order. Render as locked so the
  // player understands they need to walk forward.
  return 'locked';
}

// Continuous vertical rail that runs the full length of the level
// column, drawn behind the LevelRow coins. Split visually into two
// segments. Bright gold from row 1 to the player's current level
// (the "trodden path"), dim slate from there to the end (the "road
// ahead"). One element per segment means the rail is continuous.
// no per-row dashed gaps caused by row-flex spacing.
function ContinuousRail({ max, playerLevel }) {
  // Rail sits at the horizontal center of the 4.2cqw coin column.
  const railLeft = `calc(4.2cqw / 2 - 0.11cqw)`;
  const fraction = Math.max(0, Math.min(1, playerLevel / max));
  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        left: railLeft,
        top: '3cqw',
        bottom: '3cqw',
        width: '0.22cqw',
        background: '#3a3328',
        zIndex: 1,
        borderRadius: 'var(--radius-xs)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: `${fraction * 100}%`,
          background: '#8a6322',
          boxShadow: '0 0 6px rgba(212, 168, 67, 0.45)',
          borderRadius: 'var(--radius-xs)',
        }}
      />
    </div>
  );
}
